import { create } from "zustand";

/**
 * Tiny cross-tree store for the persistent onboarding coach. It carries the few
 * signals the coach needs that are produced elsewhere in the tree:
 *  - `journeyActive`: set by the coach itself once it has fetched onboarding
 *    progress; read by POnboarding so the checklist pill defers to the coach.
 *  - `wizardMode` / `wizardStep`: published by the create-event wizard
 *    (CreateEventPage) so the coach can show the right per-step tip even though
 *    `/create-event` has no OnboardingProvider in scope.
 *
 * A plain module-level zustand store (mirrors src/lib/seating/store.ts) so the
 * App-level coach, the per-shell POnboarding pill and the shell-less wizard all
 * subscribe to the same instance.
 */
export type WizardMode = "chat" | "choose" | "scratch" | "template" | "direction";

/**
 * localStorage flag set when the user explicitly re-opens the intro coach
 * (user-settings → "Restart intro"). Shared across the coach (reads it to skip
 * one-shot auto-graduate), PUserSettings (sets it) and AuthContext (clears it on
 * logout so it can't leak the coach into the next account on a shared browser).
 */
export const COACH_RESTART_FLAG_KEY = "veltric-onboarding-coach-restarted";

type CoachState = {
  journeyActive: boolean;
  wizardMode: WizardMode | null;
  wizardStep: number;
  /**
   * Ref-count of blocking full-screen / docked overlays currently open (offering
   * wizard, lane popover, appointment modal, …). The coach hides while > 0 so its
   * fixed bottom-right card (z-300) never covers a modal's action buttons. A
   * counter (not a boolean) so nested overlays don't un-hide the coach when the
   * inner one closes while the outer is still open.
   */
  overlayCount: number;
  /**
   * Ref-count of active CRM-transfer-mode surfaces (the guest list's docked
   * contact pane). Narrower than `overlayCount`: only the ONBOARDING floaters
   * (checklist panel + coach card, both fixed bottom-right z-300) hide on it —
   * they sit exactly where the pane docks and would swallow its drops. The
   * background-jobs and AI-translate cards stay visible: CRM sessions can run
   * minutes, and hiding a live import/export progress card that long would
   * make running jobs look lost.
   */
  crmModeCount: number;
  setJourneyActive: (active: boolean) => void;
  setWizard: (mode: WizardMode | null, step: number) => void;
  resetWizard: () => void;
  pushOverlay: () => void;
  popOverlay: () => void;
  pushCrmMode: () => void;
  popCrmMode: () => void;
};

export const useCoachStore = create<CoachState>((set) => ({
  journeyActive: false,
  wizardMode: null,
  wizardStep: 0,
  overlayCount: 0,
  crmModeCount: 0,
  setJourneyActive: (active) =>
    set((s) => (s.journeyActive === active ? s : { journeyActive: active })),
  setWizard: (mode, step) =>
    set((s) =>
      s.wizardMode === mode && s.wizardStep === step
        ? s
        : { wizardMode: mode, wizardStep: step },
    ),
  resetWizard: () => set({ wizardMode: null, wizardStep: 0 }),
  pushOverlay: () => set((s) => ({ overlayCount: s.overlayCount + 1 })),
  popOverlay: () => set((s) => ({ overlayCount: Math.max(0, s.overlayCount - 1) })),
  pushCrmMode: () => set((s) => ({ crmModeCount: s.crmModeCount + 1 })),
  popCrmMode: () => set((s) => ({ crmModeCount: Math.max(0, s.crmModeCount - 1) })),
}));
